import { tags, tagHighlighter, highlightTree } from '@lezer/highlight';
export { tags } from '@lezer/highlight';
import { parser as parser$4 } from '@lezer/cpp';
import { parser as parser$5 } from '@lezer/css';
import { parser as parser$9 } from '@lezer/go';
import { parser as parser$6 } from '@lezer/html';
import { parser as parser$3 } from '@lezer/java';
import { parser } from '@lezer/javascript';
import { parser as parser$1 } from '@lezer/json';
import { parser as parser$c } from '@lezer/markdown';
import { parser as parser$a } from '@lezer/php';
import { parser as parser$2 } from '@lezer/python';
import { parser as parser$8 } from '@lezer/rust';
import { parser as parser$d } from '@lezer/sass';
import { parser as parser$7 } from '@lezer/xml';
import { parser as parser$b } from '@lezer/yaml';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var NewLineSymbol;
(function (NewLineSymbol) {
    NewLineSymbol[NewLineSymbol["CRLF"] = 1] = "CRLF";
    NewLineSymbol[NewLineSymbol["CR"] = 2] = "CR";
    NewLineSymbol[NewLineSymbol["LF"] = 3] = "LF";
    NewLineSymbol[NewLineSymbol["NEWLINE"] = 4] = "NEWLINE";
    NewLineSymbol[NewLineSymbol["NORMAL"] = 5] = "NORMAL";
    NewLineSymbol[NewLineSymbol["NULL"] = 6] = "NULL";
})(NewLineSymbol || (NewLineSymbol = {}));
var DiffModeEnum;
(function (DiffModeEnum) {
    // github like
    DiffModeEnum[DiffModeEnum["SplitGitHub"] = 1] = "SplitGitHub";
    // gitlab like
    DiffModeEnum[DiffModeEnum["SplitGitLab"] = 2] = "SplitGitLab";
    DiffModeEnum[DiffModeEnum["Split"] = 3] = "Split";
    DiffModeEnum[DiffModeEnum["Unified"] = 4] = "Unified";
})(DiffModeEnum || (DiffModeEnum = {}));

const processAST = (ast) => {
    let lineNumber = 1;
    const syntaxObj = {};
    const loopAST = (nodes, wrapper) => {
        nodes.forEach((node) => {
            if (node.type === "text") {
                if (node.value.indexOf("\n") === -1) {
                    const valueLength = node.value.length;
                    if (!syntaxObj[lineNumber]) {
                        node.startIndex = 0;
                        node.endIndex = valueLength - 1;
                        const item = {
                            value: node.value,
                            lineNumber,
                            valueLength,
                            nodeList: [{ node, wrapper }],
                        };
                        syntaxObj[lineNumber] = item;
                    }
                    else {
                        node.startIndex = syntaxObj[lineNumber].valueLength;
                        node.endIndex = node.startIndex + valueLength - 1;
                        syntaxObj[lineNumber].value += node.value;
                        syntaxObj[lineNumber].valueLength += valueLength;
                        syntaxObj[lineNumber].nodeList.push({ node, wrapper });
                    }
                    node.lineNumber = lineNumber;
                    return;
                }
                const lines = node.value.split("\n");
                node.children = node.children || [];
                for (let i = 0; i < lines.length; i++) {
                    const _value = i === lines.length - 1 ? lines[i] : lines[i] + "\n";
                    const _lineNumber = i === 0 ? lineNumber : ++lineNumber;
                    const _valueLength = _value.length;
                    const _node = {
                        type: "text",
                        value: _value,
                        startIndex: Infinity,
                        endIndex: Infinity,
                        lineNumber: _lineNumber,
                    };
                    if (!syntaxObj[_lineNumber]) {
                        _node.startIndex = 0;
                        _node.endIndex = _valueLength - 1;
                        const item = {
                            value: _value,
                            lineNumber: _lineNumber,
                            valueLength: _valueLength,
                            nodeList: [{ node: _node, wrapper }],
                        };
                        syntaxObj[_lineNumber] = item;
                    }
                    else {
                        _node.startIndex = syntaxObj[_lineNumber].valueLength;
                        _node.endIndex = _node.startIndex + _valueLength - 1;
                        syntaxObj[_lineNumber].value += _value;
                        syntaxObj[_lineNumber].valueLength += _valueLength;
                        syntaxObj[_lineNumber].nodeList.push({ node: _node, wrapper });
                    }
                    node.children.push(_node);
                }
                node.lineNumber = lineNumber;
                return;
            }
            if (node.children) {
                loopAST(node.children, node);
                node.lineNumber = lineNumber;
            }
        });
    };
    loopAST(ast.children);
    return { syntaxFileObject: syntaxObj, syntaxFileLineNumber: lineNumber };
};

/**
 * GitHub Light / Dark token colors adapted from
 * [@uiw/codemirror-theme-github](https://github.com/uiwjs/react-codemirror/blob/master/themes/github/src/index.ts).
 *
 * Only `color` is applied (via `--diff-view-light` / `--diff-view-dark`);
 * fontWeight / fontStyle / textDecoration / backgroundColor from the CM theme
 * are intentionally omitted for DiffView span rendering.
 */
const colors = {
    tagName: { light: "#116329", dark: "#7ee787" },
    comment: { light: "#6a737d", dark: "#8b949e" },
    className: { light: "#6f42c1", dark: "#d2a8ff" },
    variableName: { light: "#005cc5", dark: "#79c0ff" },
    keyword: { light: "#d73a49", dark: "#ff7b72" },
    string: { light: "#032f62", dark: "#a5d6ff" },
    name: { light: "#22863a", dark: "#7ee787" },
    heading: { light: "#24292e", dark: "#d2a8ff" },
    atom: { light: "#e36209", dark: "#ffab70" },
    deleted: { light: "#b31d28", dark: "#ffdcd7" },
    invalid: { light: "#cb2431", dark: "#f97583" },
    foreground: { light: "#24292e", dark: "#c9d1d9" },
    inserted: { light: "#22863a", dark: "#7ee787" },
    changed: { light: "#e36209", dark: "#ffab70" },
};
/**
 * Default palette aligned with uiw GitHub theme tag groups, plus a few
 * extra common tags (definitionKeyword, function names, etc.) mapped to the
 * closest uiw color so unmatched tokens still inherit sensibly.
 */
const defaultTokenColors = [
    // uiw: [standard(tagName), tagName]
    { tag: [tags.standard(tags.tagName), tags.tagName], color: colors.tagName },
    { tag: tags.angleBracket, color: colors.tagName },
    // uiw: [comment, bracket]
    { tag: [tags.comment, tags.bracket], color: colors.comment },
    { tag: tags.lineComment, color: colors.comment },
    { tag: tags.blockComment, color: colors.comment },
    { tag: tags.docComment, color: colors.comment },
    { tag: tags.paren, color: colors.comment },
    { tag: tags.brace, color: colors.comment },
    { tag: tags.squareBracket, color: colors.comment },
    { tag: tags.punctuation, color: colors.comment },
    { tag: tags.separator, color: colors.comment },
    // uiw: [className, propertyName]
    { tag: [tags.className, tags.propertyName], color: colors.className },
    { tag: tags.definition(tags.className), color: colors.className },
    { tag: tags.definition(tags.propertyName), color: colors.className },
    { tag: tags.macroName, color: colors.className },
    // uiw: [variableName, attributeName, number, operator]
    {
        tag: [tags.variableName, tags.attributeName, tags.number, tags.operator],
        color: colors.variableName,
    },
    { tag: tags.definition(tags.variableName), color: colors.variableName },
    { tag: tags.local(tags.variableName), color: colors.variableName },
    { tag: tags.integer, color: colors.variableName },
    { tag: tags.float, color: colors.variableName },
    { tag: tags.unit, color: colors.variableName },
    { tag: tags.color, color: colors.variableName },
    { tag: tags.labelName, color: colors.variableName },
    { tag: tags.compareOperator, color: colors.variableName },
    { tag: tags.logicOperator, color: colors.variableName },
    { tag: tags.arithmeticOperator, color: colors.variableName },
    { tag: tags.bitwiseOperator, color: colors.variableName },
    { tag: tags.derefOperator, color: colors.variableName },
    { tag: tags.updateOperator, color: colors.variableName },
    { tag: tags.definitionOperator, color: colors.variableName },
    { tag: tags.controlOperator, color: colors.variableName },
    // uiw: [keyword, typeName, typeOperator]
    { tag: [tags.keyword, tags.typeName, tags.typeOperator], color: colors.keyword },
    { tag: [tags.operatorKeyword, tags.modifier, tags.controlKeyword], color: colors.keyword },
    { tag: tags.definitionKeyword, color: colors.keyword },
    { tag: tags.moduleKeyword, color: colors.keyword },
    { tag: tags.self, color: colors.keyword },
    { tag: tags.definition(tags.typeName), color: colors.keyword },
    { tag: tags.namespace, color: colors.keyword },
    // uiw: [string, meta, regexp] + url/escape/link
    { tag: [tags.string, tags.meta, tags.regexp], color: colors.string },
    { tag: [tags.url, tags.escape, tags.link], color: colors.string },
    { tag: tags.special(tags.string), color: colors.string },
    { tag: tags.docString, color: colors.string },
    { tag: tags.character, color: colors.string },
    { tag: tags.attributeValue, color: colors.string },
    { tag: tags.documentMeta, color: colors.string },
    { tag: tags.annotation, color: colors.string },
    { tag: tags.processingInstruction, color: colors.string },
    { tag: tags.monospace, color: colors.string },
    // uiw: [name, quote]
    { tag: [tags.name, tags.quote], color: colors.name },
    // uiw: [heading, strong] / emphasis
    { tag: [tags.heading, tags.strong], color: colors.heading },
    { tag: tags.heading1, color: colors.heading },
    { tag: tags.heading2, color: colors.heading },
    { tag: tags.heading3, color: colors.heading },
    { tag: tags.heading4, color: colors.heading },
    { tag: tags.heading5, color: colors.heading },
    { tag: tags.heading6, color: colors.heading },
    { tag: tags.emphasis, color: colors.heading },
    { tag: tags.content, color: colors.foreground },
    { tag: tags.contentSeparator, color: colors.comment },
    { tag: tags.list, color: colors.foreground },
    { tag: tags.strikethrough, color: colors.comment },
    // uiw: [atom, bool, special(variableName)]
    { tag: [tags.atom, tags.bool, tags.special(tags.variableName)], color: colors.atom },
    { tag: tags.null, color: colors.atom },
    { tag: tags.literal, color: colors.atom },
    { tag: tags.constant(tags.name), color: colors.atom },
    { tag: tags.standard(tags.name), color: colors.atom },
    // function names → className/purple group (common GitHub highlighting)
    { tag: tags.function(tags.variableName), color: colors.className },
    { tag: tags.function(tags.propertyName), color: colors.className },
    { tag: tags.definition(tags.function(tags.variableName)), color: colors.className },
    { tag: tags.deleted, color: colors.deleted },
    { tag: tags.inserted, color: colors.inserted },
    { tag: tags.changed, color: colors.changed },
    { tag: tags.invalid, color: colors.invalid },
];
const toStyle = (color) => `--diff-view-light:${color.light};--diff-view-dark:${color.dark}`;
const createCssVarHighlighter = (rules = defaultTokenColors) => tagHighlighter(rules.map(({ tag, color }) => ({ tag, class: toStyle(color) })));
const sameTag = (a, b) => {
    if (a === b)
        return true;
    if (Array.isArray(a) && Array.isArray(b)) {
        return a.length === b.length && a.every((item, index) => item === b[index]);
    }
    return false;
};
/**
 * Merge override rules onto a base palette.
 * Rules with the same `tag` reference are replaced; others are appended.
 */
const mergeTokenColors = (base, overrides) => {
    const filtered = base.filter((rule) => !overrides.some((item) => sameTag(rule.tag, item.tag)));
    return [...filtered, ...overrides];
};
let activeTokenColors = defaultTokenColors;
let activeHighlighter = createCssVarHighlighter(activeTokenColors);
const getTokenColors = () => activeTokenColors;
const getCssVarHighlighter = () => activeHighlighter;
/**
 * Set token colors used by `treeToHast` / `getAST`.
 *
 * @param rules Token color rules
 * @param options.extend When true, merge `rules` onto the default palette
 *   (same `tag` replaces the default rule). When false, replace the palette.
 */
const setTokenColors = (rules, options) => {
    activeTokenColors = options?.extend ? mergeTokenColors(defaultTokenColors, rules) : [...rules];
    activeHighlighter = createCssVarHighlighter(activeTokenColors);
    return activeHighlighter;
};
/**
 * Reset token colors to the built-in GitHub-like palette.
 */
const resetTokenColors = () => setTokenColors(defaultTokenColors);
/** @deprecated Use `getCssVarHighlighter()` — kept for existing imports */
const cssVarHighlighter = new Proxy({}, {
    get(_target, prop, receiver) {
        return Reflect.get(activeHighlighter, prop, receiver);
    },
});

/**
 * Convert a styled Lezer tree into hast Root.
 * Token styles use `--diff-view-light` / `--diff-view-dark` CSS variables
 * (compatible with @git-diff-view/shiki rendering).
 */
const treeToHast = (source, tree, highlighter = getCssVarHighlighter()) => {
    const children = [];
    let index = 0;
    highlightTree(tree, highlighter, (from, to, style) => {
        if (from > index) {
            children.push({ type: "text", value: source.slice(index, from) });
        }
        children.push({
            type: "element",
            tagName: "span",
            properties: style ? { style } : {},
            children: [{ type: "text", value: source.slice(from, to) }],
        });
        index = to;
    });
    if (index < source.length) {
        children.push({ type: "text", value: source.slice(index) });
    }
    return {
        type: "root",
        children: [
            {
                type: "element",
                tagName: "pre",
                properties: {},
                children: [
                    {
                        type: "element",
                        tagName: "code",
                        properties: {},
                        children,
                    },
                ],
            },
        ],
    };
};

/**
 * Wrap parser `.parse` so mixed `@lezer/common` majors (e.g. markdown vs others)
 * do not fail structural assignability during the package build.
 */
const asParse = (parse) => (input) => parse(input);
const javascript = {
    name: "javascript",
    aliases: ["js", "mjs", "cjs"],
    parse: asParse((input) => parser.parse(input)),
};
const jsx = {
    name: "jsx",
    parse: asParse((input) => parser.configure({ dialect: "jsx" }).parse(input)),
};
const typescript = {
    name: "typescript",
    aliases: ["ts", "mts", "cts"],
    parse: asParse((input) => parser.configure({ dialect: "ts" }).parse(input)),
};
const tsx = {
    name: "tsx",
    parse: asParse((input) => parser.configure({ dialect: "jsx ts" }).parse(input)),
};
const json = {
    name: "json",
    parse: asParse((input) => parser$1.parse(input)),
};
const python = {
    name: "python",
    aliases: ["py"],
    parse: asParse((input) => parser$2.parse(input)),
};
const java = {
    name: "java",
    parse: asParse((input) => parser$3.parse(input)),
};
const cpp = {
    name: "cpp",
    aliases: ["c", "c++", "h", "hpp"],
    parse: asParse((input) => parser$4.parse(input)),
};
const css = {
    name: "css",
    parse: asParse((input) => parser$5.parse(input)),
};
const html = {
    name: "html",
    aliases: ["htm"],
    parse: asParse((input) => parser$6.parse(input)),
};
const xml = {
    name: "xml",
    parse: asParse((input) => parser$7.parse(input)),
};
const rust = {
    name: "rust",
    aliases: ["rs"],
    parse: asParse((input) => parser$8.parse(input)),
};
const go = {
    name: "go",
    aliases: ["golang"],
    parse: asParse((input) => parser$9.parse(input)),
};
const php = {
    name: "php",
    parse: asParse((input) => parser$a.parse(input)),
};
const yaml = {
    name: "yaml",
    aliases: ["yml"],
    parse: asParse((input) => parser$b.parse(input)),
};
const markdown = {
    name: "markdown",
    aliases: ["md"],
    parse: asParse((input) => parser$c.parse(input)),
};
const sass = {
    name: "sass",
    aliases: ["scss"],
    parse: asParse((input) => parser$d.parse(input)),
};
const defaultLanguages = [
    javascript,
    jsx,
    typescript,
    tsx,
    json,
    python,
    java,
    cpp,
    css,
    html,
    xml,
    rust,
    go,
    php,
    yaml,
    markdown,
    sass,
];
const normalize = (lang) => lang.trim().toLowerCase();
const createLanguageRegistry = (languages = defaultLanguages) => {
    const byName = new Map();
    const register = (language) => {
        byName.set(normalize(language.name), language);
        for (const alias of language.aliases || []) {
            byName.set(normalize(alias), language);
        }
    };
    for (const language of languages) {
        register(language);
    }
    return {
        register,
        has: (lang) => byName.has(normalize(lang)),
        get: (lang) => byName.get(normalize(lang)),
        list: () => Array.from(new Set(byName.values())),
    };
};

function _getAST(_raw, _fileName, _lang, _theme) {
    if (process.env.NODE_ENV === "development") {
        console.warn("[@git-diff-view/lezer] `_getAST` is a type-level helper and is not meant to be called at runtime.");
    }
    return {};
}

let registry = createLanguageRegistry(defaultLanguages);
const rebuildRegistry = (options) => {
    const base = Array.isArray(options?.langs) && options.langs.length > 0
        ? defaultLanguages.filter((item) => options.langs.includes(item.name))
        : defaultLanguages;
    const next = createLanguageRegistry(base);
    if (Array.isArray(options?.languages)) {
        for (const language of options.languages) {
            next.register(language);
        }
    }
    registry = next;
    return registry;
};
const applyTokenColors = (options) => {
    if (!options?.tokenColors) {
        if (!options?.extendTokenColors) {
            resetTokenColors();
        }
        return;
    }
    setTokenColors(options.tokenColors, { extend: !!options.extendTokenColors });
};
const instance = { name: "lezer" };
let _maxLineToIgnoreSyntax = 2000;
const _ignoreSyntaxHighlightList = [];
Object.defineProperty(instance, "maxLineToIgnoreSyntax", {
    get: () => _maxLineToIgnoreSyntax,
});
Object.defineProperty(instance, "setMaxLineToIgnoreSyntax", {
    value: (v) => {
        _maxLineToIgnoreSyntax = v;
    },
});
Object.defineProperty(instance, "ignoreSyntaxHighlightList", {
    get: () => _ignoreSyntaxHighlightList,
});
Object.defineProperty(instance, "setIgnoreSyntaxHighlightList", {
    value: (v) => {
        _ignoreSyntaxHighlightList.length = 0;
        _ignoreSyntaxHighlightList.push(...v);
    },
});
Object.defineProperty(instance, "getAST", {
    value: (raw, fileName, lang) => {
        if (fileName &&
            highlighter.ignoreSyntaxHighlightList.some((item) => item instanceof RegExp ? item.test(fileName) : fileName === item)) {
            if (process.env.NODE_ENV === "development") {
                console.warn(`[@git-diff-view/lezer] Skipping syntax highlighting for "${fileName}" because it is listed in ignoreSyntaxHighlightList.`);
            }
            return;
        }
        const language = lang ? registry.get(lang) : undefined;
        if (!language) {
            if (process.env.NODE_ENV === "development" && lang) {
                console.warn(`[@git-diff-view/lezer] Unsupported language: "${lang}".`);
            }
            return;
        }
        try {
            const tree = language.parse(raw);
            return treeToHast(raw, tree);
        }
        catch (e) {
            if (process.env.NODE_ENV === "development") {
                console.error(e);
            }
            else {
                console.log(e.message);
            }
            return;
        }
    },
});
Object.defineProperty(instance, "processAST", {
    value: (ast) => {
        return processAST(ast);
    },
});
Object.defineProperty(instance, "hasRegisteredCurrentLang", {
    value: (lang) => {
        return registry.has(lang);
    },
});
Object.defineProperty(instance, "getHighlighterEngine", {
    value: () => registry,
});
Object.defineProperty(instance, "type", { value: "class" });
const highlighter = instance;
/**
 * Register an extra language parser on the current engine.
 */
const registerLanguage = (language) => {
    registry.register(language);
};
/**
 * get the lezer diffView highlighter
 * @returns Promise<DiffHighlighter>
 *
 * @example
 * ```ts
 * import { getDiffViewHighlighter, tags } from '@git-diff-view/lezer'
 *
 * const highlighter = await getDiffViewHighlighter({
 *   extendTokenColors: true,
 *   tokenColors: [{ tag: tags.keyword, color: { light: '#d73a49', dark: '#ff7b72' } }],
 * })
 * ```
 */
const getDiffViewHighlighter = async (options) => {
    rebuildRegistry(options);
    applyTokenColors(options);
    return highlighter;
};
const versions = "0.1.0";

export { _getAST, createCssVarHighlighter, createLanguageRegistry, cssVarHighlighter, defaultLanguages, defaultTokenColors, getCssVarHighlighter, getDiffViewHighlighter, getTokenColors, highlighter, mergeTokenColors, processAST, registerLanguage, resetTokenColors, setTokenColors, treeToHast, versions };
//# sourceMappingURL=index.mjs.map
